import React from 'react';
import styled from 'styled-components';
import {
  FiMousePointer,
  FiMinus,
  FiGitBranch,
  FiSquare,
  FiHexagon,
  FiEdit3,
  FiCircle,
  FiPieChart,
} from 'react-icons/fi';
import { DRAW_TOOLS } from '../../utils/userMarkup.js';
import { ARC_STYLES } from '../../utils/arcGeometry.js';

const Shell = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 35;
  display: flex;
  align-items: stretch;
  max-width: min(calc(100% - 24px), 920px);
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  flex-shrink: 0;
  border-radius: ${(p) => (p.$hasFlyout ? '10px 0 0 10px' : '10px')};
  background: rgba(17, 24, 39, 0.94);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);
`;

const Flyout = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  flex-shrink: 1;
  min-width: 0;
  background: rgba(17, 24, 39, 0.94);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);
  border-left: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: ${(p) => (p.$last ? '0 10px 10px 0' : '0')};
`;

const FlyoutLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: 2px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const DraftHint = styled.span`
  font-size: 11px;
  color: #d1d5db;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const ToolBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(p) => (p.$active ? '#fff' : '#d1d5db')};
  background: ${(p) => (p.$active ? '#e97024' : 'transparent')};

  &:hover {
    background: ${(p) => (p.$active ? '#d9651f' : 'rgba(255,255,255,0.1)')};
    color: #fff;
  }
`;

const ModeBtn = styled.button`
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${(p) => (p.$active ? '#fff' : '#d1d5db')};
  background: ${(p) => (p.$active ? '#e97024' : 'rgba(255, 255, 255, 0.08)')};

  &:hover {
    color: #fff;
    background: ${(p) => (p.$active ? '#d9651f' : 'rgba(255,255,255,0.14)')};
  }
`;

const DimInput = styled.input`
  width: 88px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  color: #f9fafb;
  font-size: 11px;
  flex-shrink: 0;

  &::placeholder {
    color: #9ca3af;
  }
`;

const ActionBtn = styled.button`
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${(p) => (p.$primary ? '#fff' : '#f3f4f6')};
  background: ${(p) => (p.$primary ? '#e97024' : 'rgba(255, 255, 255, 0.12)')};

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${(p) => (p.$primary ? '#d9651f' : 'rgba(255,255,255,0.18)')};
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.15);
  margin: 0 4px;
`;

const TOOLS = [
  { id: DRAW_TOOLS.SELECT, icon: FiMousePointer, label: 'Select (V)', hotkey: 'V' },
  { id: DRAW_TOOLS.LINE, icon: FiMinus, label: 'Line (L)', hotkey: 'L' },
  { id: DRAW_TOOLS.POLYLINE, icon: FiGitBranch, label: 'Polyline (P)', hotkey: 'P' },
  { id: DRAW_TOOLS.RECTANGLE, icon: FiSquare, label: 'Rectangle (R)', hotkey: 'R' },
  { id: DRAW_TOOLS.POLYGON, icon: FiHexagon, label: 'Polygon (G)', hotkey: 'G' },
  { id: DRAW_TOOLS.FREEFORM, icon: FiEdit3, label: 'Freeform (F)', hotkey: 'F' },
  { id: DRAW_TOOLS.ARC, icon: FiPieChart, label: 'Arc (A)', hotkey: 'A' },
  { id: DRAW_TOOLS.CIRCLE, icon: FiCircle, label: 'Circle (O)', hotkey: 'O' },
];

export default function DrawToolbar({
  activeTool,
  onToolChange,
  arcDrawMode = ARC_STYLES.CIRCULAR,
  onArcDrawModeChange,
  draftVisible = false,
  draftHint = '',
  draftPointCount = 0,
  draftCanFinish = false,
  onDraftFinish,
  onDraftCancel,
  showDimensionInput = false,
  dimensionInputAutoFocus = false,
  dimensionInput = '',
  onDimensionInputChange,
  dimensionPlaceholder = 'Length',
  dimensionInputTitle = 'Type length, press Enter to place',
  copyModifierEnabled = false,
  onDimensionCommit,
}) {
  const showArcFlyout = activeTool === DRAW_TOOLS.ARC;
  const showDraftFlyout = draftVisible && (
    activeTool !== DRAW_TOOLS.SELECT || showDimensionInput || Boolean(draftHint)
  );
  const hasFlyout = showArcFlyout || showDraftFlyout;

  const draftText = draftHint || 'Drawing…';
  const pointSuffix = draftPointCount > 0
    ? ` · ${draftPointCount} point${draftPointCount === 1 ? '' : 's'}`
    : '';

  return (
    <Shell>
      <Bar role="toolbar" aria-label="Drawing tools" $hasFlyout={hasFlyout}>
        {TOOLS.map((tool, idx) => {
          const Icon = tool.icon;
          return (
            <React.Fragment key={tool.id}>
              {idx === 1 && <Divider />}
              <ToolBtn
                type="button"
                $active={activeTool === tool.id}
                title={tool.label}
                aria-label={tool.label}
                onClick={() => onToolChange(tool.id)}
              >
                <Icon size={18} />
              </ToolBtn>
            </React.Fragment>
          );
        })}
      </Bar>
      {showArcFlyout && (
        <Flyout role="group" aria-label="Arc draw mode" $last={!showDraftFlyout}>
          <FlyoutLabel>Arc</FlyoutLabel>
          <ModeBtn
            type="button"
            $active={arcDrawMode === ARC_STYLES.CIRCULAR}
            title="Center, start on arc, end on arc"
            onClick={() => onArcDrawModeChange?.(ARC_STYLES.CIRCULAR)}
          >
            Circular
          </ModeBtn>
          <ModeBtn
            type="button"
            $active={arcDrawMode === ARC_STYLES.BULGE}
            title="Start, end, bulge control point"
            onClick={() => onArcDrawModeChange?.(ARC_STYLES.BULGE)}
          >
            Bulge
          </ModeBtn>
        </Flyout>
      )}
      {showDraftFlyout && (
        <Flyout role="status" aria-live="polite" $last>
          {showDimensionInput && (
            <DimInput
              value={dimensionInput}
              autoFocus={dimensionInputAutoFocus}
              onChange={(e) => onDimensionInputChange?.(e.target.value)}
              onKeyDown={(e) => {
                if (copyModifierEnabled && (e.key === 'c' || e.key === 'C')) {
                  e.preventDefault();
                  return;
                }
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onDimensionCommit?.();
                }
              }}
              placeholder={dimensionPlaceholder}
              title={dimensionInputTitle}
              aria-label={dimensionInputTitle}
            />
          )}
          <DraftHint title={`${draftText}${pointSuffix}`}>
            {draftText}
            {pointSuffix}
          </DraftHint>
          {draftCanFinish && (
            <ActionBtn type="button" $primary onClick={onDraftFinish}>
              Finish
            </ActionBtn>
          )}
          <ActionBtn type="button" onClick={onDraftCancel}>
            Cancel
          </ActionBtn>
        </Flyout>
      )}
    </Shell>
  );
}

export { TOOLS as DRAW_TOOLBAR_ITEMS };
